import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { 
  Match, 
  Bet, 
  User, 
  MatchStatus, 
  BetStatus, 
  UserTier,
  ScoreOdds 
} from '@/lib/index';

interface BetSlipItem {
  matchId: string;
  selection: string;
  amount: number;
  oddsAtTime: number;
}

/**
 * Optimized useBetting hook with reduced API calls and better error handling
 */
export function useBetting() {
  const { user, isCompany, isAgent, isPlayer } = useAuth();

  // Helper: return Authorization header with session token
  const authHeaders = () => {
    const token = user?.sessionToken;
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [matches, setMatches] = useState<Match[]>([]);
  const [bets, setBets] = useState<Bet[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [betSlip, setBetSlip] = useState<BetSlipItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastLoadTime, setLastLoadTime] = useState<number>(0);

  // Throttle data loading to prevent resource exhaustion
  const LOAD_THROTTLE_MS = 2000; // 2 seconds minimum between loads

  // Extract score odds from database match object
  const extractScoreOdds = useCallback((dbMatch: any): ScoreOdds => {
    // Extract score odds from JSONB column
    if (dbMatch.score_odds && typeof dbMatch.score_odds === 'object') {
      console.log('Extracting scoreOdds from JSONB:', dbMatch.score_odds);
      console.log('0-0 in extracted data:', dbMatch.score_odds['0-0']);
      console.log('AOS in extracted data:', dbMatch.score_odds['AOS']);
      return dbMatch.score_odds;
    }
    
    // If score_odds is a string (shouldn't happen with new backend), parse it
    if (dbMatch.score_odds && typeof dbMatch.score_odds === 'string') {
      try {
        const parsed = JSON.parse(dbMatch.score_odds);
        console.log('Parsed scoreOdds from string:', parsed);
        return parsed;
      } catch (e) {
        console.error('Failed to parse score_odds:', e);
      }
    }
    
    console.warn('No valid score_odds found in match:', dbMatch.id);
    return {};
  }, []);

  // Load matches and bets
  const loadMatches = useCallback(async () => {
    if (!user?.id) return;

    try {
      console.log('Loading matches...');

      const { data: matchData, error: matchError } = await supabase.functions.invoke(
        `match_mgmt_2026_06_07?action=list&userId=${user.id}`,
        { method: 'GET', headers: authHeaders() }
      );

      if (matchError) {
        console.error('Error loading matches:', matchError);
        return;
      }

      if (matchData?.success) {
        const transformedMatches: Match[] = matchData.matches.map((dbMatch: any) => ({
          id: dbMatch.id,
          matchNumber: dbMatch.match_number || undefined,
          homeTeam: dbMatch.home_team,
          awayTeam: dbMatch.away_team,
          odds: {
            home: parseFloat(dbMatch.home_odds || '0'),
            away: parseFloat(dbMatch.away_odds || '0'),
            draw: parseFloat(dbMatch.draw_odds || '0')
          },
          scoreOdds: extractScoreOdds(dbMatch),
          startTime: dbMatch.start_time,
          status: dbMatch.status as MatchStatus,
          result: dbMatch.result,
          companyId: dbMatch.company_id || undefined,
          createdBy: dbMatch.created_by || undefined,
        }));
        setMatches(transformedMatches);
        console.log('Loaded matches:', transformedMatches.length);
      }
    } catch (error) {
      console.error('Failed to load matches:', error);
    }
  }, [user?.id, extractScoreOdds]);

  // Load bets from database
  const loadBets = useCallback(async () => {
    if (!user?.id) return;

    try {
      console.log('Loading bets...');
      
      const { data: betData, error: betError } = await supabase.functions.invoke('betting_with_cutoff_2026_02_10_21_10', {
        method: 'GET',
        headers: authHeaders()
      });

      if (betError) {
        console.error('Error loading bets:', betError);
        return;
      }

      if (betData?.success) {
        console.log('Raw bet data from database:', betData.bets);
        const transformedBets: Bet[] = betData.bets.map((dbBet: any) => ({
          id: dbBet.id,
          userId: dbBet.user_id,
          matchId: dbBet.match_id,
          selection: dbBet.selection,
          amount: parseFloat(dbBet.amount || '0'),
          oddsAtTime: parseFloat(dbBet.odds_at_time || '0'),
          status: dbBet.status,
          createdAt: dbBet.created_at,
          updatedAt: dbBet.updated_at,
          transactionId: dbBet.transaction_id || undefined,
          remark: dbBet.remark || undefined,
          ticketPrice: parseFloat(dbBet.ticket_price || '0'),
          fightPercentage: parseFloat(dbBet.fight_percentage || '0'),
          ...(dbBet.fight_pct_snapshot != null ? { fightPctSnapshot: parseInt(dbBet.fight_pct_snapshot) } : {}),
          ...(dbBet.fight_pct_snapshot_chain != null ? { fightPctSnapshotChain: parseFloat(dbBet.fight_pct_snapshot_chain) } : {}),
          ...(dbBet.sold_out_snapshot != null ? { soldOutSnapshot: dbBet.sold_out_snapshot } : {}),
        }));
        console.log('Transformed bets:', transformedBets);
        setBets(transformedBets);
        console.log('Loaded bets:', transformedBets.length);
      }
    } catch (error) {
      console.error('Failed to load bets:', error);
    }
  }, [user?.id]);

  // Load data with throttling
  const loadData = useCallback(async () => {
    if (!user?.id) return;

    const now = Date.now();
    if (now - lastLoadTime < LOAD_THROTTLE_MS) {
      console.log('Throttling data load request');
      return;
    }

    try {
      setIsLoading(true);
      setLastLoadTime(now);
      
      // Load matches and bets
      await loadMatches();
      await loadBets();
      
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, lastLoadTime, loadMatches, loadBets]);

  // Set up real-time subscription for matches table
  useEffect(() => {
    if (!user?.id) return;

    console.log('Setting up real-time subscription for matches table');

    // Subscribe to changes in matches table
    const matchesChannel = supabase
      .channel('matches-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'matches'
        },
        (payload) => {
          console.log('Matches table changed:', payload);
          // Reload matches when any change occurs
          loadMatches();
        }
      )
      .subscribe();

    // Subscribe to changes in bets table
    const betsChannel = supabase
      .channel('bets-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'bets'
        },
        (payload) => {
          console.log('Bets table changed:', payload);
          // Reload bets when any change occurs
          loadBets();
        }
      )
      .subscribe();

    // Cleanup subscriptions on unmount
    return () => {
      console.log('Cleaning up matches and bets subscriptions');
      supabase.removeChannel(matchesChannel);
      supabase.removeChannel(betsChannel);
    };
  }, [user?.id, loadMatches, loadBets]);

  // Load data on mount and user change (single effect, no throttle conflict)
  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get matches by date
  const getMatchesByDate = useCallback((date: Date) => {
    return matches.filter(match => {
      const matchDate = new Date(match.startTime);
      return matchDate.toDateString() === date.toDateString();
    });
  }, [matches]);

  // Check if match is locked for betting
  const isMatchLocked = useCallback((match: Match) => {
    const now = new Date();
    const startTime = new Date(match.startTime);
    return startTime <= now || match.status !== MatchStatus.UPCOMING;
  }, []);

  // Company/Agent: Add Match
  const addMatch = useCallback(async (matchData: Omit<Match, 'id'>) => {
    if (!isCompany()) return;
    
    try {
      setIsLoading(true);
      console.log('Creating match:', matchData);
      
      const { data, error } = await supabase.functions.invoke('match_mgmt_2026_06_07?action=create', {
        method: 'POST',
        headers: authHeaders(),
        body: {
          userId: user?.id,
          homeTeam: matchData.homeTeam,
          awayTeam: matchData.awayTeam,
          startTime: matchData.startTime,
          status: matchData.status,
          scoreOdds: matchData.scoreOdds
        }
      });

      if (error) {
        console.error('Edge Function error:', error);
        throw new Error('Failed to create match: ' + error.message);
      }

      if (!data?.success) {
        console.error('API error:', data);
        throw new Error(data?.error || 'Failed to create match');
      }

      console.log('Match created successfully:', data.match);
      
      // Reload matches after creation
      await loadMatches();
      
    } catch (error) {
      console.error('Failed to add match:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [loadMatches]);

  // Company/Agent: Update Match
  const updateMatch = useCallback(async (matchId: string, updates: Partial<Match>) => {
    if (!isCompany()) return;
    
    try {
      setIsLoading(true);
      console.log('Updating match:', matchId, updates);
      
      const { data, error } = await supabase.functions.invoke('match_mgmt_2026_06_07?action=update', {
        method: 'PUT',
        headers: authHeaders(),
        body: {
          matchId,
          updates
        }
      });

      if (error) {
        console.error('Edge Function error:', error);
        throw new Error('Failed to update match: ' + error.message);
      }

      if (!data?.success) {
        console.error('API error:', data);
        throw new Error(data?.error || 'Failed to update match');
      }

      console.log('Match updated successfully:', data.match);
      console.log('Returned match scoreOdds:', data.match?.scoreOdds);
      console.log('Returned 0-0:', data.match?.scoreOdds?.['0-0']);
      console.log('Returned AOS:', data.match?.scoreOdds?.['AOS']);
      
      // Reload matches after update
      await loadMatches();
      
    } catch (error) {
      console.error('Failed to update match:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [loadMatches]);

  // Company/Agent: Delete Match
  const deleteMatch = useCallback(async (matchId: string) => {
    if (!isCompany()) return;
    
    try {
      setIsLoading(true);
      console.log('Deleting match:', matchId);
      
      const { data, error } = await supabase.functions.invoke('match_mgmt_2026_06_07?action=delete', {
        method: 'DELETE',
        headers: authHeaders(),
        body: { matchId }
      });

      if (error) {
        console.error('Edge Function error:', error);
        throw new Error('Failed to delete match: ' + error.message);
      }

      if (!data?.success) {
        console.error('API error:', data);
        throw new Error(data?.error || 'Failed to delete match');
      }

      console.log('Match deleted successfully');
      
      // Reload matches after deletion
      await loadMatches();
      
    } catch (error) {
      console.error('Failed to delete match:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isCompany, loadMatches]);

  // Simplified betting functions (placeholder for now)
  const addToSlip = useCallback((matchId: string, selection: string, odds: number) => {
    setBetSlip(prev => {
      const existing = prev.find(item => item.matchId === matchId);
      if (existing) {
        return prev.map(item => 
          item.matchId === matchId 
            ? { ...item, selection, oddsAtTime: odds }
            : item
        );
      }
      return [...prev, { matchId, selection, amount: 0, oddsAtTime: odds }];
    });
  }, []);

  const removeFromSlip = useCallback((matchId: string) => {
    setBetSlip(prev => prev.filter(item => item.matchId !== matchId));
  }, []);

  const clearSlip = useCallback(() => {
    setBetSlip([]);
  }, []);

  // Get available dates with matches
  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    matches.forEach(match => {
      const matchDate = new Date(match.startTime);
      dates.add(matchDate.toDateString());
    });
    return Array.from(dates).map(dateStr => new Date(dateStr));
  }, [matches]);

  // Placeholder functions for betting operations
  const placeBets = useCallback(async () => {
    console.log('Place bets - placeholder');
  }, []);

  const submitBets = useCallback(async (amounts?: Record<number, number>) => {
    if (!user?.id || betSlip.length === 0) {
      console.log('No bets to submit');
      return;
    }

    try {
      console.log('Submitting bets:', betSlip);
      
      // Prepare bets for submission
      const betsToSubmit = betSlip.map((bet, index) => ({
        matchId: bet.matchId,
        selection: bet.selection,
        amount: amounts?.[index] || 1.00, // Default amount if not provided
        oddsAtTime: bet.oddsAtTime
      }));

      const { data, error } = await supabase.functions.invoke('betting_with_cutoff_2026_02_10_21_10', {
        method: 'POST',
        body: {
          userId: user.id,
          bets: betsToSubmit
        }
      });

      if (error) {
        console.error('Error submitting bets:', error);
        throw new Error('Failed to submit bets: ' + error.message);
      }

      if (!data?.success) {
        console.error('API error:', data);
        throw new Error(data?.error || 'Failed to submit bets');
      }

      console.log('Bets submitted successfully:', data);
      
      // Clear bet slip and reload data
      clearSlip();
      await loadBets();
      
    } catch (error) {
      console.error('Failed to submit bets:', error);
      throw error;
    }
  }, [user?.id, betSlip, clearSlip, loadBets]);

  // Direct bet submission for individual matches
  // Returns the array of placed bets from the server (each has transaction_id)
  // Returns the full server response { bets, soldOutItems, hasSoldOut, transactionId, remainingCredit, allSoldOut }
  const submitDirectBets = useCallback(async (bets: Array<{matchId: string, selection: string, amount: number, oddsAtTime: number}>, remark?: string): Promise<any> => {
    if (!user?.id || bets.length === 0) {
      console.log('No bets to submit or user not authenticated');
      return { bets: [], soldOutItems: [], hasSoldOut: false };
    }

    try {
      const { data, error } = await supabase.functions.invoke('final_betting_persistence_2026_02_10_18_25?action=place_bets', {
        method: 'POST',
        body: {
          userId: user.id,
          bets: bets,
          ...(remark ? { remark } : {})
        }
      });

      if (error) {
        throw new Error('Failed to submit bets: ' + error.message);
      }

      if (!data?.success) {
        const err = new Error(data?.error || 'Failed to submit bets') as any;
        throw err;
      }

      // Reload bets to update history
      await loadBets();

      // Return full server response so caller can access soldOutItems etc.
      return data;

    } catch (error) {
      console.error('Failed to submit direct bets:', error);
      throw error;
    }
  }, [user?.id, loadBets]);

  // Cancel a bet — also voids all sibling bets with same transaction_id
  // Returns server response including newCreditByUser so caller can patch credit immediately
  const cancelBet = useCallback(async (betId: string): Promise<any> => {
    try {
      const { data, error } = await supabase.functions.invoke('cancel_bet_2026_05_12', {
        method: 'POST',
        body: { betId },
      });
      if (error) throw new Error('Failed to cancel bet: ' + error.message);
      if (!data?.success) throw new Error(data?.error || 'Failed to cancel bet');
      await loadBets();
      return data; // includes newCreditByUser
    } catch (err) {
      console.error('cancelBet error:', err);
      throw err;
    }
  }, [loadBets]);

  const updateMatchResult = useCallback(async (matchId: string) => {
    console.log('Update match result - placeholder');
  }, []);

  const settleBets = useCallback(async (matchId: string) => {
    console.log('Settle bets - placeholder');
  }, []);

  const updateBalance = useCallback(async (userId: string, newBalance: number) => {
    console.log('Update balance - placeholder');
  }, []);

  return {
    // Data
    matches,
    bets,
    users,
    betSlip,
    isLoading,
    availableDates,
    
    // Match operations
    addMatch,
    updateMatch,
    deleteMatch,
    getMatchesByDate,
    isMatchLocked,
    
    // Betting operations
    addToSlip,
    removeFromSlip,
    clearSlip,
    placeBets,
    submitBets,
    submitDirectBets,
    cancelBet,
    
    // Admin operations
    updateMatchResult,
    settleBets,
    updateBalance,
    submitMatchResult: async (matchId: string, result: string) => {
      try {
        console.log('Submitting match result:', matchId, result);
        
        const { data, error } = await supabase.functions.invoke('settlement_v2_fixed_2026_04_12_02_00', {
          method: 'POST',
          headers: authHeaders(),
          body: {
            matchId: matchId,
            result: result
          }
        });

        if (error) {
          console.error('Error submitting match result:', error);
          throw new Error('Failed to submit result: ' + error.message);
        }

        if (!data?.success) {
          console.error('API error:', data);
          throw new Error(data?.error || 'Failed to submit result');
        }

        console.log('Match result submitted successfully:', data);
        
        // Reload both matches and bets to update settled status and match result
        await loadMatches();
        await loadBets();
        
        return data;
      } catch (error) {
        console.error('Failed to submit match result:', error);
        throw error;
      }
    },
    
    // Data loading
    loadData: loadMatches, // Only load matches for now
    loadBets
  };
}